import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAlertActions, useLoadingActions } from "@/store";
import {
  SignInContainer,
  FormContainer,
  SubmitButton,
  BackNavIcon,
  RegularText,
  TitleText,
  ButtonWrapper,
  AuthTitleBlock,
  FormWrapper,
} from "./Auth.elements";
import { doPasswordReset } from "../../../firebase/auth";
import UserService from "@/Service/UserService";
import { TextField } from "@mui/material";
import { IoArrowBack } from "react-icons/io5";

const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const email = watch("email");
  const canSubmit = Boolean(email?.trim());

  const { showLoading, hideLoading } = useLoadingActions();
  const { showAlert } = useAlertActions();

  const onSubmit = async (data) => {
    const normalizedEmail = data.email.trim().toLowerCase();
    try {
      showLoading();
      try {
        // Reset via our backend: the email is sent from our own SMTP, which is far
        // less likely to land in spam than Firebase's default sender, and disabled
        // accounts get told the real reason instead of a useless reset link.
        await UserService.requestPasswordReset(normalizedEmail);
      } catch (backendError) {
        // Backend unavailable — fall back to Firebase's own reset email.
        console.error("Backend password reset failed, falling back to Firebase:", backendError);
        await doPasswordReset(normalizedEmail);
      }
      hideLoading();
      navigate("/passwordsent", { state: { email: normalizedEmail } });
    } catch (error) {
      hideLoading();
      if (error?.code === "auth/too-many-requests") {
        showAlert(
          "error",
          "Too many reset requests for this account. Please wait a few minutes before trying again — then use only the newest email you receive."
        );
      } else {
        showAlert("error", "Failed to send password reset email: " + error.message);
      }
    }
  };

  return (
      <SignInContainer>
        <BackNavIcon onClick={() => navigate("/signin")}>
          <IoArrowBack />
        </BackNavIcon>

        <FormContainer>
          <AuthTitleBlock>
            <TitleText>FORGOT PASSWORD</TitleText>
          </AuthTitleBlock>

          <RegularText>
            No worries! Enter your email address below, and we’ll send you a link to reset your password.
          </RegularText>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormWrapper style={{ width: "100%" }}>
              <TextField
                  fullWidth
                  label="Email"
                  placeholder="Enter your email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                      message: "Invalid email address",
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message || " "}
              />
            </FormWrapper>

            <ButtonWrapper>
              <SubmitButton buttonState="auth" type="submit" disabled={!canSubmit || isSubmitting}>
                Submit
              </SubmitButton>
            </ButtonWrapper>
          </form>
        </FormContainer>
      </SignInContainer>
  );
};

export default ForgotPasswordForm;
